import React, {useState} from 'react';
import cx from 'classnames';

import type {Quality} from '@pexip/peer-connection-stats';

import {
    AspectRatioSelfview,
    type SelfviewAspectRatioProps,
} from '../AspectRatioSelfview/AspectRatioSelfview';

import {InMeetingSelfviewHeader} from './InMeetingSelfviewHeader.view';
import {InMeetingSelfviewFooter} from './InMeetingSelfviewFooter.view';

import styles from './InMeetingSelfview.module.scss';

export const InMeetingSelfview: React.FC<
    SelfviewAspectRatioProps & {
        quality: Quality;
        overlayClassName?: string;
        isAudioInputMuted: boolean;
        isAudioInputDisabled?: boolean;
        isMobileDevice?: boolean;
        areEffectsEnabled: boolean;
        areEffectsApplied: boolean;
        isMaximized?: boolean;
        forceHover?: boolean; // Used only in Storybook to trigger hover styles, userEvent.hover does not trigger css :hover
        onCollapseSelfview: () => void;
        toggleFacingMode?: () => void;
        openEffects: () => void;
        setIsMaximized?: React.Dispatch<React.SetStateAction<boolean>>;
        effectsManagerView?: React.ReactElement | null;
    }
> = ({
    overlayClassName,
    isAudioInputMuted,
    isAudioInputDisabled,
    isMobileDevice,
    quality,
    forceHover = false,
    areEffectsEnabled,
    areEffectsApplied,
    isMaximized,
    onCollapseSelfview,
    toggleFacingMode,
    openEffects,
    setIsMaximized,
    effectsManagerView,
    ...props
}) => {
    const [controlsVisible, setControlsVisible] = useState(!isMobileDevice);

    const handleTouch = () => {
        setControlsVisible(v => !v);
    };

    return (
        <AspectRatioSelfview
            isMobileDevice={isMobileDevice}
            aspectRatioType="in-meeting"
            overlayClassName={cx(styles.selfviewOverlay, overlayClassName, {
                [styles.forceHover]: forceHover,
            })}
            onTouchStart={handleTouch}
            showOverlayShadow={
                isMobileDevice && (controlsVisible || isMaximized)
            }
            {...props}
        >
            {effectsManagerView}
            {!effectsManagerView && (
                <>
                    <InMeetingSelfviewHeader
                        onClick={onCollapseSelfview}
                        isMobileDevice={isMobileDevice}
                        isMaximized={isMaximized}
                        setIsMaximized={setIsMaximized}
                        controlsVisible={controlsVisible}
                        openEffects={openEffects}
                        areEffectsApplied={areEffectsApplied}
                        areEffectsEnabled={areEffectsEnabled}
                    />
                    <InMeetingSelfviewFooter
                        isAudioInputMuted={isAudioInputMuted}
                        isAudioInputDisabled={isAudioInputDisabled}
                        isMobileDevice={isMobileDevice}
                        quality={quality}
                        areEffectsApplied={areEffectsApplied}
                        toggleFacingMode={toggleFacingMode}
                        controlsVisible={controlsVisible}
                        isMaximized={isMaximized}
                        setIsMaximized={setIsMaximized}
                    />
                </>
            )}
        </AspectRatioSelfview>
    );
};

export type InMeetingSelfviewProps = React.ComponentProps<
    typeof InMeetingSelfview
>;
